/**
 * F1: every keyboard shortcut on one sheet (night goal 2026-09-25, Plan 1).
 * Many actions had only a hover tooltip or a key nobody was told about. Since the
 * single confirm (2026-10-05) Ctrl+Enter is the document's one main button, from
 * anywhere on the screen; Enter in a field and Y no longer confirm anything.
 * The list itself is shortcut-list.ts; a window a panel was moved to shows only
 * the keys that work there (shortcuts audit, 2026-10-07).
 */

import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { OPEN_SHORTCUTS_EVENT, hostHasShortcut, hostShortcutRow } from '../paige/host-shortcuts';
import { WorkspaceDialog } from './WorkspaceDialog';
import { EmailAsk, useEmailAsks } from './EmailAsk';
import { shortcutGroups, shortcutSheetKey, type ShortcutWindow } from './shortcut-list';
import { anyWindowOpen } from './review-main-action';

const Key = ({ children }: { children: string }) => <kbd className="cp-key">{children}</kbd>;
// Keys pressed together sit side by side; alternatives are separated by a slash.
const keys = (combo: string) =>
  combo.split(' / ').map((alternative, index) => (
    <span key={alternative} className="shortcut-combo">
      {index > 0 && (
        <span className="shortcut-or" aria-label="or">
          /
        </span>
      )}
      {alternative.split('+').map((part) => (
        <Key key={part}>{part}</Key>
      ))}
    </span>
  ));

/** Its foot offers "Email me this sheet" in the main window, while the email asks are on (shared/email-asks.ts). */
export function ShortcutSheet({
  onClose,
  where = 'main',
}: {
  onClose: () => void;
  where?: ShortcutWindow;
}) {
  const asks = useEmailAsks();
  return (
    <WorkspaceDialog telemetry="shortcuts" title="Keyboard shortcuts" onClose={onClose}>
      <div className="shortcut-sheet">
        {shortcutGroups(where).map((group) => (
          <section key={group.title} aria-label={group.title}>
            <h3>{group.title}</h3>
            <dl>
              {group.rows
                // PAIGE: keys of a screen or a window this host does not have are left out, and a
                // browser's document step is Alt, and F2 names the job switcher (paige/host-shortcuts.ts).
                .filter(hostHasShortcut)
                .map(hostShortcutRow)
                .map((row) => (
                  <div key={`${row.keys}:${row.action}`} className="shortcut-row">
                    <dt>
                      {row.action}
                      {row.note && <small>{row.note}</small>}
                    </dt>
                    <dd>{keys(row.keys)}</dd>
                  </div>
                ))}
            </dl>
          </section>
        ))}
      </div>
      {where === 'main' && asks?.available && (
        <div className="shortcut-sheet-foot">
          <EmailAsk kind="shortcut-sheet" view={asks} done={asks.shortcutSheet} />
        </div>
      )}
    </WorkspaceDialog>
  );
}

/**
 * F1 opens and closes the sheet in this window (the main window, and since 2026-10-07 every
 * window a panel was moved to: people with two screens press it where they are looking). ? does
 * the same when it is not typed into a text box, and leaves any other open window alone.
 */
export function useShortcutSheetKeys(setOpen: Dispatch<SetStateAction<boolean>>) {
  useEffect(() => {
    const toggle = (event: KeyboardEvent) => {
      const key = shortcutSheetKey(event);
      if (!key) return;
      const sheetOpen = Boolean(document.querySelector('[data-telemetry-dialog="shortcuts"]'));
      if (key === '?' && !sheetOpen && anyWindowOpen()) return;
      event.preventDefault();
      setOpen((open) => !open);
    };
    window.addEventListener('keydown', toggle);
    // PAIGE: the status bar's keyboard button and Paige's Help open the sheet too (paige/host-shortcuts.ts).
    const show = () => setOpen(true);
    window.addEventListener(OPEN_SHORTCUTS_EVENT, show);
    return () => {
      window.removeEventListener('keydown', toggle);
      window.removeEventListener(OPEN_SHORTCUTS_EVENT, show);
    };
  }, [setOpen]);
}
